import { viewerStrings } from '../copy/strings.js';
import { message, CopyError } from '../copy/text.js';
import { baseQuery } from '@cdr-clarity/query-service';
import {
  queryLimits,
  type QueryDefinition,
  type QueryExpression,
  type RelationshipField,
  type RelationshipQueryDefinition,
} from '@cdr-clarity/contracts';
import { RecordsState, type RecordsSnapshot } from './records-state.js';
import { predicateFields } from './query-references.js';
import {
  unifiedRecordPage,
  type ExecuteRelationshipQuery,
  type UnifiedRecordPage,
} from './unified-record-page.js';

export interface UnifiedRecordsSnapshot extends RecordsSnapshot {
  readonly availableFields: readonly RelationshipField[];
  readonly visibleFields: readonly RelationshipField[];
}
function mapPredicate(predicate: QueryExpression, map: (id: string) => string): QueryExpression {
  if ('args' in predicate)
    return { ...predicate, args: predicate.args.map((arg) => mapPredicate(arg, map)) };
  if ('arg' in predicate) return { ...predicate, arg: mapPredicate(predicate.arg, map) };
  return { ...predicate, field: map(predicate.field) };
}

/** Association presentation shares canonical preferences and source selection with ordinary records. */
export class UnifiedRecordsState {
  readonly fields;
  private snapshot: UnifiedRecordsSnapshot;
  private readonly listeners = new Set<() => void>();
  private readonly stops: (() => void)[];
  private identity = '';
  private cursors = new Map<number, readonly string[] | null>([[0, null]]);
  constructor(
    readonly records: RecordsState,
    private readonly catalog: readonly RelationshipField[],
    private readonly execute: ExecuteRelationshipQuery,
  ) {
    this.fields = records.fields;
    this.snapshot = this.readSnapshot();
    const update = () => {
      this.snapshot = this.readSnapshot();
      for (const listener of this.listeners) listener();
    };
    this.stops = [records.subscribe(update), this.fields.subscribe(update)];
  }
  private qualified = (id: string): string => {
    const exact = this.catalog.find((field) => field.id === id);
    const field =
      exact ??
      this.catalog.find((field) => field.sourceField === id && field.binding === 'primary') ??
      this.catalog.find((field) => field.sourceField === id);
    if (!field)
      throw new CopyError(
        message(
          viewerStrings.webStateUnifiedRecordsState.fieldIsUnavailableInTheAssociationCatalog,
          { value1: id },
        ),
      );
    return field.id;
  };
  private readSnapshot(): UnifiedRecordsSnapshot {
    const state = this.records.getSnapshot();
    const fields = this.fields.getSnapshot();
    const visible = new Set(fields.visible.map((field) => field.id));
    const visibleFields = this.catalog.filter((field) => visible.has(field.sourceField));
    const permitted = new Set(visibleFields.map((field) => field.id));
    const refs = [
      ...(!state.focus && fields.preference.predicate
        ? predicateFields(fields.preference.predicate)
        : []),
      ...fields.preference.sort.map((item) => item.key),
    ];
    const invalidFields = refs.filter((id) => {
      try {
        return !permitted.has(this.qualified(id));
      } catch {
        return true;
      }
    });
    return {
      ...state,
      availableFields: this.catalog,
      visibleFields,
      invalidFields: [...new Set(invalidFields)],
    };
  }
  getSnapshot = (): UnifiedRecordsSnapshot => this.snapshot;
  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };
  definition(): RelationshipQueryDefinition {
    if (this.snapshot.invalidFields.length)
      throw new CopyError(
        viewerStrings.webStateUnifiedRecordsState.thisAssociationQueryUsesHiddenOrUnavailable,
      );
    const { preference } = this.fields.getSnapshot();
    return {
      ...this.reportDefinition(),
      visibleFields: this.fields.getSnapshot().visible.map((field) => field.id),
      order: preference.sort.map((item) => ({ ...item, key: this.qualified(item.key) })),
    };
  }
  /** Full report catalog; the saved table layout never restricts report inputs. */
  reportDefinition(): RelationshipQueryDefinition {
    const { preference } = this.fields.getSnapshot();
    const state = this.records.getSnapshot();
    const {
      expansion: _expansion,
      window: _window,
      ...base
    } = baseQuery({
      ...state.scope,
      collection: 'cdr',
      ...(state.focus ? { recordIds: [...state.focus] } : {}),
      excludedRecordIds: state.focus ? [] : [...state.hidden],
    });
    void _expansion;
    void _window;
    return {
      ...base,
      kind: 'relationship-projection',
      relationship: { type: 'media', targetCollection: 'cmr', includeUnmatched: true },
      visibleFields: this.fields.getSnapshot().available.map((field) => field.id),
      measures: [],
      order: [],
      page: { size: preference.pageSize },
      ...(!state.focus && preference.predicate
        ? { predicate: mapPredicate(preference.predicate, this.qualified) }
        : {}),
    };
  }
  ordinaryDefinition(): QueryDefinition {
    return this.ordinary(this.definition());
  }
  ordinaryReportDefinition(): QueryDefinition {
    return this.ordinary(this.reportDefinition());
  }
  private ordinary(definition: RelationshipQueryDefinition): QueryDefinition {
    const source = (id: string) => {
      const field = this.catalog.find((field) => field.id === id)!;
      if (field.binding !== 'primary')
        throw new CopyError(
          viewerStrings.webStateUnifiedRecordsState.thisFilterOrSortUsesMediaFields,
        );
      return field.sourceField;
    };
    return {
      ...baseQuery(definition.scope),
      page: definition.page,
      order: definition.order.map((item) => ({ ...item, key: source(item.key) })),
      ...(definition.predicate ? { predicate: mapPredicate(definition.predicate, source) } : {}),
    };
  }
  async page(
    offset: number,
    size: number,
    order: QueryDefinition['order'],
  ): Promise<UnifiedRecordPage> {
    if (
      !Number.isSafeInteger(size) ||
      size < 1 ||
      size > queryLimits.responseRows ||
      !Number.isSafeInteger(offset) ||
      offset < 0 ||
      offset % size !== 0
    )
      throw new CopyError(
        viewerStrings.webStateUnifiedRecordsState.requestedAssociationBlockDoesNotAlignWith,
      );
    const mapped = order.map((item) => ({ ...item, key: this.qualified(item.key) }));
    const columns = this.snapshot.visibleFields.map((field) => field.id);
    if (mapped.some((item) => !columns.includes(item.key)))
      throw new CopyError(
        viewerStrings.webStateUnifiedRecordsState.sortingUsesHiddenOrUnavailableAssociationFields,
      );
    const definition = { ...this.definition(), order: mapped, page: { size } };
    const identity = JSON.stringify({ definition, columns });
    if (identity !== this.identity) {
      this.identity = identity;
      this.cursors = new Map([[0, null]]);
    }
    const generation = this.snapshot.generation;
    let start = Math.max(...[...this.cursors.keys()].filter((index) => index <= offset));
    while (true) {
      const page = await unifiedRecordPage(
        this.execute,
        definition,
        columns,
        this.cursors.get(start)!,
      );
      if (generation !== this.snapshot.generation || identity !== this.identity)
        throw new CopyError(
          viewerStrings.webStateUnifiedRecordsState.theAssociationQueryChangedWhileLoadingThis,
        );
      if (page.nextCursors) this.cursors.set(start + size, page.nextCursors);
      if (this.cursors.size > 200)
        this.cursors.delete(
          [...this.cursors.keys()].find((index) => index !== 0 && index !== start + size)!,
        );
      if (start === offset) return page;
      if (!page.nextCursors)
        throw new CopyError(
          viewerStrings.webStateUnifiedRecordsState.theRequestedPageIsOutsideThisAssociation,
        );
      start += size;
    }
  }
  setFilter(predicate: QueryExpression | undefined): void {
    this.records.setFilter(predicate);
  }
  select(ids: Iterable<string>): void {
    this.records.select(ids);
  }
  hideSelected(): void {
    this.records.hideSelected();
  }
  showHidden(): void {
    this.records.showHidden();
  }
  focus(ids: readonly string[]): void {
    this.records.focus(ids);
  }
  returnToResults(): void {
    this.records.returnToResults();
  }
  dispose(): void {
    for (const stop of this.stops) stop();
    this.listeners.clear();
  }
}
